// Library Methods
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

// Types
import { ExamSectionReportType } from "src/services/exams/types";

// Utils
import {
  calculateDurationForDisplay,
  getTutorialCompletionText,
  getVisualField,
} from "src/utils/exams/examReportsHelper";
import { getLocalDateTimeWithZone } from "src/utils/date/dateHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

const usePatternChartColumns = (examReport?: ExamSectionReportType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const localDateAndTimeWithZone = useMemo(
    () =>
      examReport?.timing?.start
        ? getLocalDateTimeWithZone(
            examReport.timing.start,
            clinicInfo?.timezone
          )
        : { date: "N/A", time: "N/A" },
    [examReport?.timing?.start, clinicInfo?.timezone]
  );

  const patternChartColumns = useMemo(() => {
    const allColumns = [
      {
        title: t("Algorithm"),
        value: t(examReport?.exam?.algorithm ?? ""),
        isBlueText: true,
      },
      {
        title: t("Grid Type"),
        value: getVisualField(examReport?.exam?.grid),
        isBlueText: true,
      },
      {
        title: t("Tutorial"),
        value: getTutorialCompletionText(
          examReport?.postProcessing?.tutorialCompletionState,
          t
        ),
      },
      {
        title: t("Date"),
        value: localDateAndTimeWithZone?.date,
      },
      {
        title: t("Time_hour"),
        value: localDateAndTimeWithZone?.time,
      },
      {
        title: t("Duration"),
        value: examReport?.timing?.elapsed
          ? calculateDurationForDisplay(examReport?.timing?.elapsed)
          : "",
        isBlueText: true,
      },
    ];

    return allColumns;
  }, [
    localDateAndTimeWithZone,
    examReport?.exam?.algorithm,
    examReport?.exam?.grid,
    examReport?.timing?.elapsed,
    examReport?.postProcessing?.tutorialCompletionState,
    t,
  ]);

  return { patternChartColumns };
};

export default usePatternChartColumns;
